import { createSignal, For, Show, type JSX } from 'solid-js'
import { Button } from './Button'
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-solid'

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200] as const

export const TablePager = (props: {
  page: number
  pageCount: number
  total: number
  onChange: (p: number) => void
  unit?: string
  /** When provided, renders a "Righe per pagina" select. Caller is
   *  responsible for re-computing pageCount based on the new size. */
  pageSize?: number
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: readonly number[]
}): JSX.Element => {
  const unit = () => props.unit ?? 'righe'
  const sizeOptions = () => props.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS
  const showPager = () => props.pageCount > 1
  const showSize = () => props.onPageSizeChange != null && props.pageSize != null

  // ── direct page-jump popover (clamps to range, blurs to commit) ─────────
  const [jumpOpen, setJumpOpen] = createSignal(false)
  const [jumpValue, setJumpValue] = createSignal('')
  const commitJump = (): void => {
    const v = Number(jumpValue())
    if (Number.isFinite(v) && v >= 1 && v <= props.pageCount) {
      props.onChange(Math.floor(v) - 1)
    }
    setJumpOpen(false)
    setJumpValue('')
  }

  // Hide entire footer when there's nothing to do (single page + no size
  // selector). Otherwise show: total + size selector + nav cluster.
  return (
    <Show when={showPager() || showSize()}>
      <div class="flex flex-wrap items-center justify-between gap-3 text-sm text-muted mt-3">
        <div class="flex items-center gap-3">
          <span>
            {props.total} {unit()}
            <Show when={showPager()}>
              {' '}— pagina {Math.min(props.page, props.pageCount - 1) + 1} di {props.pageCount}
            </Show>
          </span>
          <Show when={showSize()}>
            <label class="flex items-center gap-2">
              <span class="text-xs">Righe</span>
              <select
                class="rounded border border-border bg-bg px-2 py-1 text-xs"
                value={String(props.pageSize)}
                onChange={(e) => props.onPageSizeChange?.(Number(e.currentTarget.value))}
              >
                <For each={sizeOptions()}>
                  {(s) => <option value={s}>{s}</option>}
                </For>
              </select>
            </label>
          </Show>
        </div>
        <Show when={showPager()}>
          <div class="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={props.page === 0}
              onClick={() => props.onChange(0)}
              aria-label="Prima pagina"
            >
              <ChevronsLeft size={14} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={props.page === 0}
              onClick={() => props.onChange(Math.max(0, props.page - 1))}
              aria-label="Pagina precedente"
            >
              <ChevronLeft size={14} />
            </Button>
            {/* Direct jump: a tiny input that commits on Enter or blur. */}
            <div class="relative">
              <Show
                when={jumpOpen()}
                fallback={
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setJumpValue(String(props.page + 1))
                      setJumpOpen(true)
                    }}
                    aria-label="Vai alla pagina"
                    title="Vai alla pagina"
                  >
                    …
                  </Button>
                }
              >
                <input
                  type="number"
                  min="1"
                  max={props.pageCount}
                  class="w-16 h-8 rounded border border-border bg-bg px-2 text-xs tabular-nums"
                  value={jumpValue()}
                  autofocus
                  onInput={(e) => setJumpValue(e.currentTarget.value)}
                  onBlur={commitJump}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      commitJump()
                    } else if (e.key === 'Escape') {
                      setJumpOpen(false)
                      setJumpValue('')
                    }
                  }}
                />
              </Show>
            </div>
            <Button
              size="sm"
              variant="ghost"
              disabled={props.page >= props.pageCount - 1}
              onClick={() => props.onChange(Math.min(props.pageCount - 1, props.page + 1))}
              aria-label="Pagina successiva"
            >
              <ChevronRight size={14} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={props.page >= props.pageCount - 1}
              onClick={() => props.onChange(props.pageCount - 1)}
              aria-label="Ultima pagina"
            >
              <ChevronsRight size={14} />
            </Button>
          </div>
        </Show>
      </div>
    </Show>
  )
}
